/**
 * 기구 무대 꾸밈 데이터(2026-10-08 · 운영자 꾸밈 편집기) — 운영 콘솔이 저장한 장면 데이터(Field 카탈로그 stage_scene payload)를
 * 렌더러가 그릴 수 있는 StageTheme 로 바꾼다. 꾸밈은 화면 전용(물리 · 판정 · 리플레이 해시와 무관)이라 여기서 거르기만 한다.
 *  - 인벤토리: 그릴 수 있는 소품 · 캐릭터 · 물건 · 원경 · 바닥 무늬 종류(편집기 팔레트가 이 목록을 쓴다)
 *  - 거르기: 모르는 종류 · 숫자 아닌 값은 빼고, 크기 · 개수는 상한으로 자르고, 받침대 둘레와 카메라 앞은 비운다(기계를 가리지 않게)
 *  - 데이터에 없는 칸(하늘 · 조명 · 안개 …)은 기본 장면(stageScenes) 값을 쓴다
 * 좌표: m · 받침대 가운데가 원점 · 무대 카메라는 +z 에서 −z 를 본다(소품은 주로 z < 0 뒤쪽 · 양옆).
 */
import type { CharacterKind, ItemKind } from "./characters";
import type {
  FarKind,
  FarLayer,
  FigureSpec,
  GroundPattern,
  PropKind,
  PropSpec,
  StageModelSpec,
  StageTheme,
} from "./stageScenes";

export const PROP_KINDS = [
  "tree",
  "pine",
  "palm",
  "cactus",
  "rock",
  "bush",
  "flowers",
  "mushroom",
  "balloons",
  "flag",
  "bunting",
  "lamp",
  "fence",
  "tent",
  "column",
  "tower",
  "ship",
  "cake",
  "helipad",
  "nest",
  "mailbox",
  "rainbow",
  "stand",
  "swingset",
] as const satisfies readonly PropKind[];

export const CHARACTER_KINDS = [
  "kid",
  "girl",
  "adult",
  "chef",
  "rescuer",
  "fairy",
  "pirate",
  "bear",
  "teddy",
  "rabbit",
  "fox",
  "squirrel",
  "hedgehog",
  "owl",
  "chick",
  "bird",
  "parrot",
  "gull",
  "monkey",
  "giraffe",
  "elephant",
  "dino",
] as const satisfies readonly CharacterKind[];

export const ITEM_KINDS = [
  "banana",
  "cupcake",
  "strawberry",
  "teapot",
  "cup",
  "coins",
  "letter",
  "helicopter",
  "camera",
  "icecream",
  "bone",
  "butterfly",
  "milk",
  "helmet",
  "skeleton",
] as const satisfies readonly ItemKind[];

export const FAR_KINDS = [
  "mountains",
  "hills",
  "forest",
  "city",
  "dunes",
  "islands",
  "fair",
  "wall",
] as const satisfies readonly FarKind[];

export const GROUND_PATTERNS = [
  "grass",
  "sand",
  "water",
  "tile",
  "wood",
  "rock",
  "moss",
] as const satisfies readonly GroundPattern[];

export const FIGURE_IDLES = ["bob", "sway", "fly", "none"] as const;

/** 꾸밈 상한 — 그리기 호출 · 손님 몸짓 계산(저사양 태블릿) */
export const STAGE_DECOR_LIMITS = { props: 40, figures: 12, far: 4, models: 8 } as const;
/** 올린 모델 키(카탈로그 키 규칙과 같다) */
export const STAGE_MODEL_KEY = /^[a-z0-9-]{1,40}$/;
/** 소품 · 손님을 둘 수 있는 바닥 범위(|x| · |z| ≤ m) — 멀리 둔 무지개 · 배(기본 장면 −9m)까지 */
export const STAGE_DECOR_AREA = 10;
/** 받침대 둘레 비우기(|x| · |z| < m — 받침대 0.25m + 손 한 뼘 · 날아다니는 손님은 높이 0.4m 넘으면 괜찮다) */
export const STAGE_DECOR_PEDESTAL = 0.4;
/** 받침대 밑에 까는 무늬라 비우기 규칙을 받지 않는 소품 */
const FLAT_UNDER_PEDESTAL: readonly string[] = ["helipad"];
/** 카메라 앞 비우기 — z 가 이보다 크고 |x| 가 FRONT_HALF 안이면 카메라와 기계 사이를 막는다 */
export const STAGE_DECOR_FRONT_Z = 0.6;
export const STAGE_DECOR_FRONT_HALF = 0.9;

export interface StageDecorIssue {
  readonly path: string;
  readonly message: string;
}

/** 운영 콘솔이 저장하는 꾸밈 데이터(stage_scene payload) — 없는 칸은 기본 장면 값 */
export interface StageDecorData {
  readonly version?: 1;
  readonly props?: readonly PropSpec[];
  readonly figures?: readonly FigureSpec[];
  readonly far?: readonly FarLayer[];
  /** 운영자가 올린 3D 모델(카탈로그 stage_model 키 · 2026-10-08) */
  readonly models?: readonly StageModelSpec[];
  readonly ground?: StageTheme["ground"];
  readonly deck?: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;
const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const r3 = (v: number) => Math.round(v * 1000) / 1000;
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** 이 바닥 자리가 비워 둘 곳인가(받침대 둘레 · 카메라 앞) — y 는 손님 높이(소품은 0) */
export function inKeepClear(x: number, z: number, y = 0): boolean {
  if (y > 0.4) return false;
  const nearPedestal = Math.abs(x) < STAGE_DECOR_PEDESTAL && Math.abs(z) < STAGE_DECOR_PEDESTAL;
  const inFront = z > STAGE_DECOR_FRONT_Z && Math.abs(x) < STAGE_DECOR_FRONT_HALF;
  return nearPedestal || inFront;
}

const inArea = (x: number, z: number) =>
  Math.abs(x) <= STAGE_DECOR_AREA && Math.abs(z) <= STAGE_DECOR_AREA;

/** 자리 규칙 — 무대(기본: ±10m · 받침대 둘레 · 카메라 앞 비우기) · 룸 자유 놀이터({@link ROOM_DECOR_RULES}) */
interface PlaceRules {
  readonly inArea: (x: number, z: number) => boolean;
  readonly keepClear: (x: number, z: number, y?: number) => boolean;
  readonly outside: string;
}
const STAGE_RULES: PlaceRules = {
  inArea,
  keepClear: inKeepClear,
  outside: `무대 밖(±${STAGE_DECOR_AREA}m)`,
};

/** 룸 자유 놀이터 바닥(2026-10-11 · 교사 꾸밈) — 24 × 20 m · 가운데 z = 2(룸 서버 SANDBOX_FLOOR) · 기계가 지나가도 되는 보기 전용이라 비우기 규칙 없음 */
export const ROOM_DECOR_FLOOR = { x: 24, z: 20, cz: 2 } as const;
const ROOM_DECOR_RULES: PlaceRules = {
  inArea: (x, z) =>
    Math.abs(x) <= ROOM_DECOR_FLOOR.x / 2 &&
    Math.abs(z - ROOM_DECOR_FLOOR.cz) <= ROOM_DECOR_FLOOR.z / 2,
  keepClear: () => false,
  outside: "놀이터 바닥 밖",
};

function readProp(
  v: unknown,
  at: string,
  issues: StageDecorIssue[],
  rules: PlaceRules = STAGE_RULES,
): PropSpec | null {
  if (!isObj(v)) return (issues.push({ path: at, message: "객체가 아니에요" }), null);
  const kind = v.kind as PropKind;
  if (!(PROP_KINDS as readonly string[]).includes(kind as string))
    return (issues.push({ path: `${at}.kind`, message: `모르는 소품 ${String(v.kind)}` }), null);
  const p = v.at;
  if (!Array.isArray(p) || !num(p[0]) || !num(p[1]))
    return (issues.push({ path: `${at}.at`, message: "자리 [x, z] 가 필요해요" }), null);
  const x = p[0],
    z = p[1];
  if (!rules.inArea(x, z)) return (issues.push({ path: `${at}.at`, message: rules.outside }), null);
  if (rules.keepClear(x, z) && !FLAT_UNDER_PEDESTAL.includes(kind))
    return (
      issues.push({ path: `${at}.at`, message: "받침대 둘레 · 카메라 앞은 비워 둬요" }),
      null
    );
  const out: { -readonly [K in keyof PropSpec]: PropSpec[K] } = { kind, at: [r3(x), r3(z)] };
  if (num(v.scale)) out.scale = r3(clamp(v.scale, 0.3, 3));
  if (typeof v.color === "string" && HEX.test(v.color)) out.color = v.color;
  if (num(v.rot)) out.rot = Math.round(((v.rot % 360) + 360) % 360);
  if (kind === "bunting") {
    const to = v.to;
    if (Array.isArray(to) && num(to[0]) && num(to[1]) && rules.inArea(to[0], to[1]))
      out.to = [r3(to[0]), r3(to[1])];
    else
      return (issues.push({ path: `${at}.to`, message: "만국기는 끝점 [x, z] 가 필요해요" }), null);
  }
  return out;
}

function readFigure(
  v: unknown,
  at: string,
  issues: StageDecorIssue[],
  rules: PlaceRules = STAGE_RULES,
): FigureSpec | null {
  if (!isObj(v)) return (issues.push({ path: at, message: "객체가 아니에요" }), null);
  const char = v.char as CharacterKind | undefined;
  const item = v.item as ItemKind | undefined;
  const okChar = char !== undefined && (CHARACTER_KINDS as readonly string[]).includes(char);
  const okItem = item !== undefined && (ITEM_KINDS as readonly string[]).includes(item);
  if (!okChar && !okItem)
    return (issues.push({ path: at, message: `모르는 손님 ${String(v.char ?? v.item)}` }), null);
  const p = v.at;
  if (!Array.isArray(p) || !num(p[0]) || !num(p[1]) || !num(p[2]))
    return (issues.push({ path: `${at}.at`, message: "자리 [x, y, z] 가 필요해요" }), null);
  const x = p[0],
    y = clamp(p[1], 0, 2),
    z = p[2];
  if (!rules.inArea(x, z)) return (issues.push({ path: `${at}.at`, message: rules.outside }), null);
  if (rules.keepClear(x, z, y))
    return (
      issues.push({ path: `${at}.at`, message: "받침대 둘레 · 카메라 앞은 비워 둬요" }),
      null
    );
  const size = num(v.size) ? r3(clamp(v.size, 0.05, 1.5)) : 0.3;
  const out: { -readonly [K in keyof FigureSpec]: FigureSpec[K] } = {
    ...(okChar ? { char } : { item }),
    at: [r3(x), r3(y), r3(z)],
    size,
  };
  if (typeof v.color === "string" && HEX.test(v.color)) out.color = v.color;
  if ((FIGURE_IDLES as readonly unknown[]).includes(v.idle))
    out.idle = v.idle as FigureSpec["idle"];
  if (typeof v.cheers === "boolean") out.cheers = v.cheers;
  const f = v.face;
  if (Array.isArray(f) && num(f[0]) && num(f[1])) out.face = [r3(f[0]), r3(f[1])];
  return out;
}

function readModel(
  v: unknown,
  at: string,
  issues: StageDecorIssue[],
  rules: PlaceRules = STAGE_RULES,
): StageModelSpec | null {
  if (!isObj(v)) return (issues.push({ path: at, message: "객체가 아니에요" }), null);
  if (typeof v.model !== "string" || !STAGE_MODEL_KEY.test(v.model))
    return (issues.push({ path: `${at}.model`, message: `모르는 모델 ${String(v.model)}` }), null);
  const p = v.at;
  if (!Array.isArray(p) || !num(p[0]) || !num(p[1]) || !num(p[2]))
    return (issues.push({ path: `${at}.at`, message: "자리 [x, y, z] 가 필요해요" }), null);
  const x = p[0],
    y = clamp(p[1], 0, 2),
    z = p[2];
  if (!rules.inArea(x, z)) return (issues.push({ path: `${at}.at`, message: rules.outside }), null);
  if (rules.keepClear(x, z, y))
    return (
      issues.push({ path: `${at}.at`, message: "받침대 둘레 · 카메라 앞은 비워 둬요" }),
      null
    );
  return {
    model: v.model,
    at: [r3(x), r3(y), r3(z)],
    size: num(v.size) ? r3(clamp(v.size, 0.05, 3)) : 0.5,
    ...(num(v.rot) ? { rot: Math.round(((v.rot % 360) + 360) % 360) } : {}),
  };
}

function readFar(v: unknown, at: string, issues: StageDecorIssue[]): FarLayer | null {
  if (!isObj(v)) return (issues.push({ path: at, message: "객체가 아니에요" }), null);
  if (!(FAR_KINDS as readonly unknown[]).includes(v.kind))
    return (issues.push({ path: `${at}.kind`, message: `모르는 원경 ${String(v.kind)}` }), null);
  if (typeof v.color !== "string" || !HEX.test(v.color))
    return (issues.push({ path: `${at}.color`, message: "색 #RRGGBB 가 필요해요" }), null);
  return {
    kind: v.kind as FarKind,
    color: v.color,
    dist: num(v.dist) ? r3(clamp(v.dist, 5, 40)) : 20,
    height: num(v.height) ? r3(clamp(v.height, 0.5, 15)) : 4,
    seed: num(v.seed) ? Math.round(clamp(v.seed, 0, 9999)) : 1,
    ...(v.snow === true ? { snow: true } : {}),
    ...(typeof v.lights === "string" && HEX.test(v.lights) ? { lights: v.lights } : {}),
  };
}

/**
 * 꾸밈 데이터 → 그릴 장면. base(기본 장면)의 하늘 · 조명 · 안개 · 받침대 꾸밈은 그대로 두고 소품 · 손님 · 원경 · 바닥만 데이터로 바꾼다.
 * 데이터에 칸이 없으면 base 그대로 — 잘못된 항목은 빼고 issues 로 알린다(편집기가 보여 준다).
 */
export function normalizeStageDecor(
  data: unknown,
  base: StageTheme,
): { theme: StageTheme; issues: StageDecorIssue[] } {
  const issues: StageDecorIssue[] = [];
  if (!isObj(data)) {
    if (data != null) issues.push({ path: "", message: "꾸밈 데이터가 객체가 아니에요" });
    return { theme: base, issues };
  }
  const list = <T>(
    key: string,
    max: number,
    read: (v: unknown, at: string, issues: StageDecorIssue[]) => T | null,
  ): T[] | null => {
    const arr = data[key];
    if (arr === undefined) return null;
    if (!Array.isArray(arr)) return (issues.push({ path: key, message: "목록이 아니에요" }), []);
    if (arr.length > max)
      issues.push({ path: key, message: `${max}개까지만 그려요(${arr.length}개)` });
    return arr
      .slice(0, max)
      .map((v, i) => read(v, `${key}[${i}]`, issues))
      .filter((x): x is T => x !== null);
  };
  const props = list("props", STAGE_DECOR_LIMITS.props, readProp);
  const figures = list("figures", STAGE_DECOR_LIMITS.figures, readFigure);
  const far = list("far", STAGE_DECOR_LIMITS.far, readFar);
  const models = list("models", STAGE_DECOR_LIMITS.models, readModel);
  let ground = base.ground;
  if (isObj(data.ground)) {
    const g = data.ground;
    if (
      (GROUND_PATTERNS as readonly unknown[]).includes(g.pattern) &&
      typeof g.color === "string" &&
      HEX.test(g.color) &&
      typeof g.color2 === "string" &&
      HEX.test(g.color2)
    )
      ground = { pattern: g.pattern as GroundPattern, color: g.color, color2: g.color2 };
    else issues.push({ path: "ground", message: "바닥은 무늬 · 색 두 개(#RRGGBB)가 필요해요" });
  }
  const deck = typeof data.deck === "string" && HEX.test(data.deck) ? data.deck : base.deck;
  return {
    theme: {
      ...base,
      ...(props ? { props } : {}),
      ...(figures ? { figures } : {}),
      ...(far ? { far } : {}),
      ...(models ? { models } : {}),
      ground,
      deck,
    },
    issues,
  };
}

/** 기본 장면 → 편집기 시작 데이터(소품 · 손님 · 원경 · 바닥 · 무대 판 색) */
export function stageDecorOf(theme: StageTheme): StageDecorData {
  return {
    version: 1,
    props: theme.props,
    figures: theme.figures,
    far: theme.far,
    models: theme.models ?? [],
    ground: theme.ground,
    deck: theme.deck,
  };
}

/**
 * 룸 자유 놀이터 교사 꾸밈(2026-10-11) — 소품 · 손님(캐릭터 · 물건) · 올린 모델만(원경 · 바닥 · 하늘은 룸 장면 그대로).
 * 자리는 놀이터 바닥(룸 좌표 m) · 상한은 무대와 같다. 보기 전용 — 물리 · 판정 · 리플레이 해시와 무관(룸 서버는 그대로 실어 나른다).
 */
export interface RoomDecorData {
  readonly props?: readonly PropSpec[];
  readonly figures?: readonly FigureSpec[];
  readonly models?: readonly StageModelSpec[];
}
export function readRoomDecor(data: unknown): {
  decor: Required<RoomDecorData>;
  issues: StageDecorIssue[];
} {
  const issues: StageDecorIssue[] = [];
  const empty = { props: [], figures: [], models: [] };
  if (!isObj(data)) {
    if (data != null) issues.push({ path: "", message: "꾸밈 데이터가 객체가 아니에요" });
    return { decor: empty, issues };
  }
  const list = <T>(
    key: string,
    max: number,
    read: (v: unknown, at: string, issues: StageDecorIssue[], rules: PlaceRules) => T | null,
  ): T[] => {
    const arr = data[key];
    if (arr === undefined) return [];
    if (!Array.isArray(arr)) return (issues.push({ path: key, message: "목록이 아니에요" }), []);
    if (arr.length > max)
      issues.push({ path: key, message: `${max}개까지만 그려요(${arr.length}개)` });
    return arr
      .slice(0, max)
      .map((v, i) => read(v, `${key}[${i}]`, issues, ROOM_DECOR_RULES))
      .filter((x): x is T => x !== null);
  };
  return {
    decor: {
      props: list("props", STAGE_DECOR_LIMITS.props, readProp),
      figures: list("figures", STAGE_DECOR_LIMITS.figures, readFigure),
      models: list("models", STAGE_DECOR_LIMITS.models, readModel),
    },
    issues,
  };
}
